import { utils } from '@digital-commerce/shared-utils'
import {
  pipe,
  evolve,
  map,
  path,
  prop,
  filter,
  anyPass,
  includes,
  complement,
  allPass,
  sortBy,
  memoizeWith,
  isNil
} from 'ramda'
import { isMonthlyRecurringAmountNotZero, getCacheKey } from '../../../utils/cart'
import { formatCurrency } from '../../../utils/currency'
import { buildHSNonRecurringDiscounts } from '../../../utils/discount'
import { getSubItemSortingWeight, getTVExtras } from '../../../utils/homeSolutions'

const { getHSService, getDisplayName, getMonthlyItemPrice } = utils.cart

const AUTH_CHANGE_URL = '/my-account/manage-services/home/pik/packages'
const UNAUTH_CHANGE_URL = '/shop/home-services/bundle/build-your-own'

export const getPikTVService = memoizeWith(getCacheKey('HS-PikTV'), (cart) => {
  if (!cart?.root?.items) return undefined

  return pipe(
    getHSService(['tvx', 'tv']),
    evolve({
      items: map((id) => path(['items', id], cart))
    })
  )(cart)
})

export const getChangeUrl = (isAuthenticated) => {
  if (isAuthenticated) return AUTH_CHANGE_URL
  return UNAUTH_CHANGE_URL
}

/**
 * Gets a sorted list of included sub items from top-level Pik TV service offer.
 * @param {Object} service - PikTV service item
 * @returns {Object[]} List of sub items
 */
const getPikTVSubItems = pipe(
  prop('items'),
  filter(
    pipe(
      prop('tags'),
      anyPass([
        allPass([includes('tv'), includes('tvx'), includes('channel')]),
        allPass([includes('tv'), includes('tvx'), includes('pack')])
      ])
    )
  ),
  filter(complement(isMonthlyRecurringAmountNotZero)),
  sortBy(getSubItemSortingWeight)
)

const buildSubItemsList = (subItems, lang) => {
  const mapper = (featureItem) => {
    const { id } = featureItem
    const name = getDisplayName(featureItem)
    const price = `${formatCurrency(getMonthlyItemPrice(featureItem).subTotal, lang)}/mo.`

    return {
      id,
      name,
      price
    }
  }

  return subItems?.map(mapper)
}

export const buildSubItemsData = (service, lang) => {
  const subItems = getPikTVSubItems(service)
  return buildSubItemsList(subItems, lang)
}

export const buildTVExtrasData = (service, lang) => {
  const extras = getTVExtras(service)
  return buildSubItemsList(extras, lang)
}

export const buildNonRecurringDiscounts = (service, lang) => {
  // Top-level non-recurring discounts
  const topLevel = buildHSNonRecurringDiscounts([service], lang)
  // Sub-item level non-recurring discounts
  const subLevel = buildHSNonRecurringDiscounts(service.items, lang)

  return [...topLevel, ...subLevel]
}

export const shouldRender = (cart) => {
  const service = getPikTVService(cart)
  return !isNil(service)
}
